/**
 * Universal Import Service
 *
 * Supports multiple import formats:
 * - OFX/QFX (bank standard)
 * - QIF (Quicken)
 * - CSV (smart column detection + bank presets)
 * - JSON (NinjaCash backup)
 *
 * Features:
 * - Transaction fingerprinting for deduplication
 * - FITID extraction from OFX for bank transaction IDs
 * - Smart column detection for arbitrary CSV formats
 * - Bank preset templates for popular banks
 */

// @ts-expect-error - ofx-js has no type declarations
import * as ofxJs from 'ofx-js';
import { deserializeQif, QifData } from 'qif-ts';
import Papa from 'papaparse';
import { db, Transaction } from './db';
import { transactionService, TransactionInput } from './transactions';
import { generateId } from '../utils/id';
import { devlog } from './devlog';
import { dataEvents } from './dataEvents';
import { syncManager } from './syncManager';

// ============================================================
// Types
// ============================================================

export type ImportFormat = 'ofx' | 'qfx' | 'qif' | 'csv' | 'json' | 'unknown';

export interface ParsedTransaction {
  date: Date;
  amount: number;
  type: 'income' | 'expense' | 'transfer';
  description: string;
  categoryName?: string;
  accountName?: string;
  toAccountName?: string;
  fitId?: string;           // OFX bank transaction ID
  fingerprint: string;      // Hash for deduplication
  checkNumber?: string;
  memo?: string;
  rawData?: unknown;        // Original parsed data for debugging
}

export interface ImportPreview {
  format: ImportFormat;
  fileName: string;
  fileSize: number;
  totalTransactions: number;
  transactions: ParsedTransaction[];
  duplicateCount: number;
  newCount: number;
  duplicateFingerprints: Set<string>;
  suggestedAccount?: string;
  columnMapping?: ColumnMapping;     // For CSV files
  bankPreset?: BankPreset;           // Detected or selected bank
  errors: string[];
  warnings: string[];
  // NinjaCash full backup detection
  isNinjaCashBackup?: boolean;       // True if JSON contains accounts + transactions (full backup)
  backupData?: NinjaCashBackupData;  // Parsed backup data for full restore
  // 1Money CSV detection
  is1MoneyFormat?: boolean;          // True if CSV has 1Money-specific columns (FROM ACCOUNT, NOTES, etc.)
  rawFileContent?: string;           // Store raw content for 1Money import
}

// NinjaCash backup format (from exportData())
export interface NinjaCashBackupData {
  accounts: Array<{
    id: string;
    name: string;
    fullName?: string;
    type: string;
    currency: string;
    balance: number;
    initialBalance?: number;  // BUG-058 FIX: Starting balance not derived from transactions
    isActive: boolean;
    sortOrder?: number;
    createdAt: string | Date;
    updatedAt: string | Date;
    syncStatus?: string;
  }>;
  transactions: Array<{
    id: string;
    accountId: string;
    type: string;
    amount: number;
    categoryId?: string;
    toAccountId?: string;
    description: string;
    date: string | Date;
    currency: string;
    createdAt: string | Date;
    updatedAt: string | Date;
    syncStatus?: string;
    deletedAt?: string | Date | null;
    scheduledTransactionId?: string;
    wasSkipped?: boolean;
    importFingerprint?: string;
    importFitId?: string;
    importSource?: string;
  }>;
  categories: Array<{
    id: string;
    name: string;
    type: string;
    icon?: string;
    color?: string;
    parentId?: string;
    isActive: boolean;
    createdAt?: string | Date;
    updatedAt?: string | Date;
  }>;
  budgets?: unknown[];
  scheduledTransactions?: Array<{
    id: string;
    accountId: string;
    categoryId?: string;
    toAccountId?: string;
    type: string;
    amount: number;
    description?: string;
    frequency: string;
    dayOfMonth?: number;
    dayOfWeek?: number;
    monthOfYear?: number;
    nextDueDate: string | Date;
    lastGeneratedDate?: string | Date;
    isPaused?: boolean;
    pausedUntil?: string | Date;
    isIgnoredPermanently?: boolean;
    endDate?: string | Date;
    autoDetected?: boolean;
    detectionKey?: string;
    dismissCount?: number;
    createdAt?: string | Date;
    updatedAt?: string | Date;
    deletedAt?: string | Date | null;
  }>;
  skippedOccurrences?: Array<{
    id: string;
    scheduledTransactionId: string;
    skippedDate: string | Date;
    reason?: string;
    createdAt?: string | Date;
  }>;
  exportedAt?: string;
}

export interface UniversalImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  duplicatesSkipped: number;
  errors: string[];
  warnings: string[];
  newAccounts: string[];
  newCategories: string[];
}

export interface UniversalImportOptions {
  accountId?: string;              // Target account for import
  skipDuplicates?: boolean;        // Skip transactions with existing fingerprints
  bankPresetId?: string;           // Use specific bank preset for CSV
  columnMapping?: ColumnMapping;   // Custom column mapping for CSV
  importSource?: string;           // Source identifier for tracking
}

// ============================================================
// Column Mapping for CSV
// ============================================================

export interface ColumnMapping {
  date: string | null;
  amount: string | null;
  description: string | null;
  type?: string | null;           // Optional: income/expense column
  category?: string | null;
  debit?: string | null;          // For separate debit column
  credit?: string | null;         // For separate credit column
  balance?: string | null;        // Running balance (for validation)
  checkNumber?: string | null;
  memo?: string | null;
}

export interface BankPreset {
  id: string;
  name: string;
  country: string;
  columnMapping: ColumnMapping;
  dateFormat: string;             // e.g., 'MM/DD/YYYY', 'YYYY-MM-DD'
  amountMultiplier?: number;      // -1 if negative means expense
  skipRows?: number;              // Header rows to skip
  hasHeader?: boolean;
  encoding?: string;
}

// ============================================================
// Bank Presets - Popular Banks
// ============================================================

export const BANK_PRESETS: BankPreset[] = [
  // US Banks
  {
    id: 'chase-us',
    name: 'Chase Bank',
    country: 'US',
    columnMapping: {
      date: 'Posting Date',
      amount: 'Amount',
      description: 'Description',
      type: 'Type',
      balance: 'Balance',
    },
    dateFormat: 'MM/DD/YYYY',
    hasHeader: true,
  },
  {
    id: 'bofa-us',
    name: 'Bank of America',
    country: 'US',
    columnMapping: {
      date: 'Date',
      amount: 'Amount',
      description: 'Description',
    },
    dateFormat: 'MM/DD/YYYY',
    hasHeader: true,
  },
  {
    id: 'wells-fargo-us',
    name: 'Wells Fargo',
    country: 'US',
    columnMapping: {
      date: 'Date',
      amount: 'Amount',
      description: 'Description',
    },
    dateFormat: 'MM/DD/YYYY',
    hasHeader: true,
  },
  {
    id: 'citi-us',
    name: 'Citi Bank',
    country: 'US',
    columnMapping: {
      date: 'Date',
      amount: null,  // Uses debit/credit columns instead
      debit: 'Debit',
      credit: 'Credit',
      description: 'Description',
    },
    dateFormat: 'MM/DD/YYYY',
    hasHeader: true,
  },
  {
    id: 'capital-one-us',
    name: 'Capital One',
    country: 'US',
    columnMapping: {
      date: 'Transaction Date',
      amount: 'Transaction Amount',
      description: 'Description',
      category: 'Category',
    },
    dateFormat: 'YYYY-MM-DD',
    hasHeader: true,
  },
  // Fintech
  {
    id: 'revolut',
    name: 'Revolut',
    country: 'Global',
    columnMapping: {
      date: 'Completed Date',
      amount: 'Amount',
      description: 'Description',
      type: 'Type',
      balance: 'Balance',
    },
    dateFormat: 'YYYY-MM-DD HH:mm:ss',
    hasHeader: true,
  },
  {
    id: 'wise',
    name: 'Wise (TransferWise)',
    country: 'Global',
    columnMapping: {
      date: 'Date',
      amount: 'Amount',
      description: 'Description',
    },
    dateFormat: 'DD-MM-YYYY',
    hasHeader: true,
  },
  {
    id: 'n26',
    name: 'N26',
    country: 'EU',
    columnMapping: {
      date: 'Date',
      amount: 'Amount (EUR)',
      description: 'Payee',
      type: 'Transaction type',
    },
    dateFormat: 'YYYY-MM-DD',
    hasHeader: true,
  },
  {
    id: 'monzo',
    name: 'Monzo',
    country: 'UK',
    columnMapping: {
      date: 'Date',
      amount: 'Amount',
      description: 'Description',
      category: 'Category',
    },
    dateFormat: 'DD/MM/YYYY',
    hasHeader: true,
  },
  // Canada
  {
    id: 'td-ca',
    name: 'TD Canada',
    country: 'Canada',
    columnMapping: {
      date: 'Date',
      amount: 'Amount',
      description: 'Description',
    },
    dateFormat: 'MM/DD/YYYY',
    hasHeader: true,
  },
  {
    id: 'rbc-ca',
    name: 'RBC Royal Bank',
    country: 'Canada',
    columnMapping: {
      date: 'Transaction Date',
      amount: 'CAD$',
      description: 'Description 1',
    },
    dateFormat: 'MM/DD/YYYY',
    hasHeader: true,
  },
  // UK
  {
    id: 'barclays-uk',
    name: 'Barclays',
    country: 'UK',
    columnMapping: {
      date: 'Date',
      amount: 'Amount',
      description: 'Description',
      memo: 'Memo',
    },
    dateFormat: 'DD/MM/YYYY',
    hasHeader: true,
  },
  {
    id: 'hsbc-uk',
    name: 'HSBC UK',
    country: 'UK',
    columnMapping: {
      date: 'Date',
      amount: 'Amount',
      description: 'Description',
    },
    dateFormat: 'DD/MM/YYYY',
    hasHeader: true,
  },
  {
    id: 'lloyds-uk',
    name: 'Lloyds Bank',
    country: 'UK',
    columnMapping: {
      date: 'Transaction Date',
      amount: null,  // Uses debit/credit columns instead
      debit: 'Debit Amount',
      credit: 'Credit Amount',
      description: 'Transaction Description',
    },
    dateFormat: 'DD/MM/YYYY',
    hasHeader: true,
  },
  // 1Money (existing format)
  {
    id: '1money',
    name: '1Money App',
    country: 'Global',
    columnMapping: {
      date: 'DATE',
      amount: 'AMOUNT',
      description: 'NOTES',
      type: 'TYPE',
      category: 'TO ACCOUNT/TO CATEGORY',
    },
    dateFormat: 'M/D/YY',
    hasHeader: true,
  },
];

// ============================================================
// Smart Column Detection Keywords
// ============================================================

const DATE_KEYWORDS = ['date', 'posted', 'trans date', 'booking', 'completed', 'transaction date', 'value date'];
const AMOUNT_KEYWORDS = ['amount', 'sum', 'value', 'total', 'transaction amount'];
const DEBIT_KEYWORDS = ['debit', 'withdrawal', 'payment', 'out'];
const CREDIT_KEYWORDS = ['credit', 'deposit', 'in'];
const DESC_KEYWORDS = ['description', 'memo', 'details', 'narrative', 'payee', 'merchant', 'name'];
const CATEGORY_KEYWORDS = ['category', 'type', 'classification'];
const BALANCE_KEYWORDS = ['balance', 'running balance', 'available'];
const CHECK_KEYWORDS = ['check', 'cheque', 'reference', 'ref'];

// ============================================================
// Auto-Categorization: Grocery/Food Stores
// ============================================================

/**
 * Known grocery/food store names for auto-categorization.
 * When a transaction description matches one of these, auto-assign "Food" category.
 * All lowercase for case-insensitive matching.
 */
const GROCERY_STORES = [
  // USA
  'kroger', 'meijer', "trader joe's", 'trader joe', 'walmart', 'target', 'costco',
  'safeway', 'whole foods', 'wholefoods', 'publix', 'aldi', 'h-e-b', 'heb', 'wegmans',
  'albertsons', 'vons', 'ralphs', 'food lion', 'giant eagle', 'stop & shop', 'stop and shop',
  'shoprite', 'price chopper', 'winco', 'food 4 less', 'grocery outlet', 'sprouts',
  // Canada
  'loblaws', 'sobeys', 'metro', 'no frills', 'superstore', 'freshco', 'save-on-foods',
  'food basics', 'longos', 'farm boy', 'real canadian superstore',
  // UK
  'tesco', "sainsbury's", 'sainsburys', 'asda', 'morrisons', 'lidl', 'waitrose',
  'co-op food', 'm&s food', 'marks & spencer food', 'iceland', 'aldi uk',
  // Germany
  'rewe', 'edeka', 'kaufland', 'penny', 'netto', 'real', 'norma',
  // China
  'freshippo', 'hema', 'rt-mart', 'carrefour', 'yonghui', 'wumart', "ole'", 'ole',
  // Japan
  'aeon', 'ito-yokado', 'life supermarket', 'seiyu', 'maruetsu', 'summit store', 'ok store',
  // South Korea
  'e-mart', 'emart', 'lotte mart', 'homeplus', 'cu', 'gs25',
  // India
  'd-mart', 'dmart', 'big bazaar', 'reliance fresh', "spencer's", 'spencers', 'more supermarket',
  // Australia/NZ
  'woolworths', 'coles', 'iga', 'countdown', "pak'nsave", 'paknsave', 'new world',
  // SEA (Southeast Asia)
  'big c', 'tesco lotus', 'sm supermarket', "robinson's", 'robinsons', 'giant', 'cold storage',
  'ntuc fairprice', 'fairprice', 'sheng siong',
  // Convenience stores (global)
  '7-eleven', '7 eleven', 'familymart', 'family mart', 'lawson', 'circle k', 'ministop',
  'am pm', 'ampm', 'wawa', 'sheetz', 'quiktrip', 'casey', 'pilot flying j',
];

/**
 * Detect if a transaction description matches a known grocery/food store.
 * Returns 'Food' if matched, undefined otherwise.
 * Only call this when categoryName is not already set.
 */
export function detectGroceryCategory(description: string): string | undefined {
  const descLower = description.toLowerCase();
  
  for (const store of GROCERY_STORES) {
    if (descLower.includes(store)) {
      return 'Food';
    }
  }
  
  return undefined;
}

// ============================================================
// Fingerprint Generation
// ============================================================

/**
 * Normalize a description for fingerprinting (v2, BUG-092).
 *
 * NFKC unifies full-width/half-width forms (ＡＢＣ = ABC, half-width kana =
 * full-width kana); `\p{L}\p{N}\p{M}` with the `u` flag keeps ALL Unicode
 * letters, numbers AND combining marks. `\p{M}` is essential: Thai vowel/tone
 * marks are combining marks, not letters - without it ค่า ("fee") becomes
 * คา (a different word) and distinct Thai descriptions can still collide.
 * The legacy `[^a-z0-9]` strip reduced every Japanese/Thai description to
 * the EMPTY string, so unrelated same-date same-amount transactions
 * false-deduplicated (silent import data loss). Truncation is by code point
 * ([...str]) so surrogate pairs (rare kanji, emoji) never split into
 * mojibake halves.
 */
export function normalizeDescriptionV2(description: string): string {
  return [
    ...description
      .normalize('NFKC')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\p{M}]/gu, ''),
  ]
    .slice(0, 50)
    .join('');
}

/** Shared 32-bit string hash (no crypto needed for this use case). */
function hashString(combined: string): string {
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Generate a fingerprint for transaction deduplication (v2: `fp2_` prefix).
 * Combines date + amount + Unicode-safe normalized description into a hash.
 *
 * BUG-092: versioned algorithm. New fingerprints are always fp2_; stored
 * legacy fp_ values are never rewritten - findDuplicateFingerprints()
 * matches them via generateLegacyFingerprint() + tuple verification.
 */
export function generateFingerprint(date: Date, amount: number, description: string): string {
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
  const amountStr = Math.abs(amount).toFixed(2);
  const combined = `${dateStr}|${amountStr}|${normalizeDescriptionV2(description)}`;
  return `fp2_${hashString(combined)}`;
}

/**
 * LEGACY (fp_) fingerprint algorithm - kept ONLY to match fingerprints
 * already stored in IndexedDB from imports before 2026-07-10.
 * NEVER write new fp_ values; never "fix" this normalization - it must
 * reproduce the historical bytes exactly (including the non-ASCII strip).
 */
export function generateLegacyFingerprint(date: Date, amount: number, description: string): string {
  const dateStr = date.toISOString().split('T')[0];
  const amountStr = Math.abs(amount).toFixed(2);
  const descNormalized = description
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '')
    .substring(0, 50);
  return `fp_${hashString(`${dateStr}|${amountStr}|${descNormalized}`)}`;
}

// ============================================================
// Format Detection
// ============================================================

/**
 * Detect import format from file content and extension.
 */
export function detectFormat(file: File, content: string): ImportFormat {
  const ext = file.name.toLowerCase().split('.').pop() || '';
  const firstLine = content.trim().split('\n')[0] || '';

  // Check file extension first
  if (ext === 'ofx' || ext === 'qfx') {
    return ext as ImportFormat;
  }
  if (ext === 'qif') {
    return 'qif';
  }
  if (ext === 'json') {
    return 'json';
  }
  if (ext === 'csv') {
    return 'csv';
  }

  // Content sniffing
  if (content.includes('OFXHEADER') || content.includes('<OFX>')) {
    return 'ofx';
  }
  if (firstLine.startsWith('!Type:') || firstLine.startsWith('!Account')) {
    return 'qif';
  }
  if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
    try {
      JSON.parse(content);
      return 'json';
    } catch {
      // Not valid JSON
    }
  }

  // Assume CSV if comma-separated
  if (firstLine.includes(',')) {
    return 'csv';
  }

  return 'unknown';
}

// ============================================================
// OFX/QFX Parser
// ============================================================

async function parseOFX(content: string): Promise<ParsedTransaction[]> {
  const transactions: ParsedTransaction[] = [];

  try {
    // ofx-js.parse returns a Promise with parsed OFX data
    const ofxData = await ofxJs.parse(content);

    // Navigate to transaction list - structure varies by OFX type
    // Bank: OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN
    // Credit Card: OFX.CREDITCARDMSGSRSV1.CCSTMTTRNRS.CCSTMTRS.BANKTRANLIST.STMTTRN
    let bankTransactions: unknown[] = [];

    // Try bank transactions
    try {
      const bankPath = ofxData?.OFX?.BANKMSGSRSV1?.STMTTRNRS?.STMTRS?.BANKTRANLIST?.STMTTRN;
      if (bankPath) {
        bankTransactions = Array.isArray(bankPath) ? bankPath : [bankPath];
      }
    } catch { /* Not a bank OFX */ }

    // Try credit card transactions
    try {
      const ccPath = ofxData?.OFX?.CREDITCARDMSGSRSV1?.CCSTMTTRNRS?.CCSTMTRS?.BANKTRANLIST?.STMTTRN;
      if (ccPath) {
        const ccTransactions = Array.isArray(ccPath) ? ccPath : [ccPath];
        bankTransactions = [...bankTransactions, ...ccTransactions];
      }
    } catch { /* Not a credit card OFX */ }

    for (const tx of bankTransactions) {
      const txObj = tx as Record<string, string | undefined>;
      const amountStr = txObj.TRNAMT;
      const amount = amountStr ? parseFloat(amountStr) : NaN;
      const date = parseOFXDate(txObj.DTPOSTED);

      if (!date || isNaN(amount)) continue;

      const type = amount >= 0 ? 'income' : 'expense';
      const description = txObj.MEMO || txObj.NAME || 'OFX Transaction';

      transactions.push({
        date,
        amount: Math.abs(amount),
        type,
        description,
        fitId: txObj.FITID,
        checkNumber: txObj.CHECKNUM,
        fingerprint: generateFingerprint(date, Math.abs(amount), description),
        rawData: tx,
      });
    }
  } catch (error) {
    devlog.error('import', 'OFX parse error', { error });
    throw new Error(`Failed to parse OFX: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return transactions;
}

function parseOFXDate(dateStr: string | undefined): Date | null {
  if (!dateStr) return null;

  // OFX date format: YYYYMMDD or YYYYMMDDHHMMSS
  const cleaned = dateStr.replace(/\[.*\]/, ''); // Remove timezone info
  const year = parseInt(cleaned.substring(0, 4), 10);
  const month = parseInt(cleaned.substring(4, 6), 10) - 1;
  const day = parseInt(cleaned.substring(6, 8), 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

  return new Date(year, month, day);
}

// ============================================================
// QIF Parser
// ============================================================

async function parseQIF(content: string): Promise<ParsedTransaction[]> {
  const transactions: ParsedTransaction[] = [];

  try {
    const qifData: QifData = deserializeQif(content);

    for (const tx of qifData.transactions) {
      const date = parseQIFDate(tx.date);
      const amount = tx.amount ?? 0;

      if (!date) continue;

      const type = amount >= 0 ? 'income' : 'expense';
      const description = tx.payee || tx.memo || 'QIF Transaction';

      transactions.push({
        date,
        amount: Math.abs(amount),
        type,
        description,
        categoryName: tx.category,
        checkNumber: tx.reference,
        memo: tx.memo,
        fingerprint: generateFingerprint(date, Math.abs(amount), description),
        rawData: tx,
      });
    }
  } catch (error) {
    devlog.error('import', 'QIF parse error', { error });
    throw new Error(`Failed to parse QIF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return transactions;
}

function parseQIFDate(dateStr: string | undefined): Date | null {
  if (!dateStr) return null;

  // QIF dates can be in various formats: M/D/YY, M/D'YY, MM/DD/YYYY
  const cleaned = dateStr.replace(/'/g, '/');
  const parts = cleaned.split('/');

  if (parts.length !== 3) return null;

  const month = parseInt(parts[0], 10) - 1;
  const day = parseInt(parts[1], 10);
  let year = parseInt(parts[2], 10);

  if (year < 100) {
    year += year > 50 ? 1900 : 2000;
  }

  if (isNaN(month) || isNaN(day) || isNaN(year)) return null;

  return new Date(year, month, day);
}

// ============================================================
// Smart CSV Column Mapper
// ============================================================

/**
 * Auto-detect column mapping from CSV headers.
 */
export function autoDetectColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {
    date: null,
    amount: null,
    description: null,
  };

  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());

  // Find date column
  mapping.date = headers.find((_, i) =>
    DATE_KEYWORDS.some(k => normalizedHeaders[i].includes(k))
  ) || null;

  // Find amount column (prefer 'amount' over debit/credit)
  mapping.amount = headers.find((_, i) =>
    AMOUNT_KEYWORDS.some(k => normalizedHeaders[i].includes(k))
  ) || null;

  // Find separate debit/credit columns if no amount
  if (!mapping.amount) {
    mapping.debit = headers.find((_, i) =>
      DEBIT_KEYWORDS.some(k => normalizedHeaders[i].includes(k))
    ) || null;

    mapping.credit = headers.find((_, i) =>
      CREDIT_KEYWORDS.some(k => normalizedHeaders[i].includes(k))
    ) || null;
  }

  // Find description column
  mapping.description = headers.find((_, i) =>
    DESC_KEYWORDS.some(k => normalizedHeaders[i].includes(k))
  ) || null;

  // Find optional columns
  mapping.category = headers.find((_, i) =>
    CATEGORY_KEYWORDS.some(k => normalizedHeaders[i].includes(k))
  ) || null;

  mapping.balance = headers.find((_, i) =>
    BALANCE_KEYWORDS.some(k => normalizedHeaders[i].includes(k))
  ) || null;

  mapping.checkNumber = headers.find((_, i) =>
    CHECK_KEYWORDS.some(k => normalizedHeaders[i].includes(k))
  ) || null;

  return mapping;
}

/**
 * Detect if CSV is specifically 1Money format.
 * 1Money has unique columns: FROM ACCOUNT, TO ACCOUNT/TO CATEGORY, NOTES
 * This is more specific than the generic bank preset detection.
 */
export function is1MoneyCSV(headers: string[]): boolean {
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
  
  // 1Money unique identifiers - must have ALL of these
  const requiredColumns = [
    'date',
    'type', 
    'from account',
    'to account/to category',
    'amount',
    'notes'
  ];
  
  const matchCount = requiredColumns.filter(col => 
    normalizedHeaders.includes(col)
  ).length;
  
  // Must have at least 5 of 6 required columns (allowing for minor variations)
  return matchCount >= 5;
}

/**
 * Detect which bank preset matches the CSV headers.
 */
export function detectBankPreset(headers: string[]): BankPreset | undefined {
  const normalizedHeaders = headers.map(h => h.toLowerCase().trim());

  for (const preset of BANK_PRESETS) {
    const mapping = preset.columnMapping;
    let matchCount = 0;
    let totalRequired = 0;

    // Check required columns
    if (mapping.date) {
      totalRequired++;
      if (normalizedHeaders.includes(mapping.date.toLowerCase())) matchCount++;
    }
    if (mapping.amount) {
      totalRequired++;
      if (normalizedHeaders.includes(mapping.amount.toLowerCase())) matchCount++;
    }
    if (mapping.description) {
      totalRequired++;
      if (normalizedHeaders.includes(mapping.description.toLowerCase())) matchCount++;
    }

    // If 80%+ match, return this preset
    if (totalRequired > 0 && matchCount / totalRequired >= 0.8) {
      return preset;
    }
  }

  return undefined;
}

// ============================================================
// CSV Parser
// ============================================================

async function parseCSV(
  content: string,
  mapping: ColumnMapping,
  preset?: BankPreset
): Promise<ParsedTransaction[]> {
  const transactions: ParsedTransaction[] = [];

  return new Promise((resolve, reject) => {
    Papa.parse(content, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(),
      complete: (result) => {
        for (const row of result.data as Record<string, string>[]) {
          try {
            const tx = parseCSVRow(row, mapping, preset);
            if (tx) transactions.push(tx);
          } catch (error) {
            devlog.warn('import', 'CSV row parse error', { row, error: String(error) });
          }
        }
        resolve(transactions);
      },
      error: (error: Error) => {
        reject(new Error(`CSV parse error: ${error.message}`));
      },
    });
  });
}

function parseCSVRow(
  row: Record<string, string>,
  mapping: ColumnMapping,
  preset?: BankPreset
): ParsedTransaction | null {
  // Get date
  const dateStr = mapping.date ? row[mapping.date] : null;
  if (!dateStr) return null;

  const date = parseFlexibleDate(dateStr, preset?.dateFormat);
  if (!date) return null;

  // Get amount
  let amount = 0;
  let type: 'income' | 'expense' = 'expense';

  if (mapping.amount) {
    const amountStr = row[mapping.amount]?.replace(/[$,]/g, '').trim();
    amount = parseFloat(amountStr || '0');

    if (preset?.amountMultiplier) {
      amount *= preset.amountMultiplier;
    }

    type = amount >= 0 ? 'income' : 'expense';
    amount = Math.abs(amount);
  } else if (mapping.debit && mapping.credit) {
    // Separate debit/credit columns
    const debitStr = row[mapping.debit]?.replace(/[$,]/g, '').trim();
    const creditStr = row[mapping.credit]?.replace(/[$,]/g, '').trim();

    const debit = parseFloat(debitStr || '0') || 0;
    const credit = parseFloat(creditStr || '0') || 0;

    if (credit > 0) {
      amount = credit;
      type = 'income';
    } else {
      amount = Math.abs(debit);
      type = 'expense';
    }
  }

  if (amount === 0) return null;

  // Get description
  const description = (mapping.description ? row[mapping.description] : '') || 'CSV Transaction';

  // Get optional fields
  const categoryName = mapping.category ? row[mapping.category] : undefined;
  const checkNumber = mapping.checkNumber ? row[mapping.checkNumber] : undefined;
  const memo = mapping.memo ? row[mapping.memo] : undefined;

  return {
    date,
    amount,
    type,
    description,
    categoryName,
    checkNumber,
    memo,
    fingerprint: generateFingerprint(date, amount, description),
    rawData: row,
  };
}

function parseFlexibleDate(dateStr: string, formatHint?: string): Date | null {
  if (!dateStr) return null;

  const cleaned = dateStr.trim();

  // Try ISO format first (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}/.test(cleaned)) {
    const d = new Date(cleaned);
    if (!isNaN(d.getTime())) return d;
  }

  // Try MM/DD/YYYY or M/D/YY
  const slashParts = cleaned.split(/[\/\-]/);
  if (slashParts.length === 3) {
    let month, day, year;

    if (formatHint?.startsWith('DD')) {
      // UK format: DD/MM/YYYY
      day = parseInt(slashParts[0], 10);
      month = parseInt(slashParts[1], 10) - 1;
      year = parseInt(slashParts[2], 10);
    } else {
      // US format: MM/DD/YYYY (default)
      month = parseInt(slashParts[0], 10) - 1;
      day = parseInt(slashParts[1], 10);
      year = parseInt(slashParts[2], 10);
    }

    if (year < 100) {
      year += year > 50 ? 1900 : 2000;
    }

    if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
      return new Date(year, month, day);
    }
  }

  // Last resort: Date.parse
  const parsed = Date.parse(cleaned);
  if (!isNaN(parsed)) {
    return new Date(parsed);
  }

  return null;
}

// ============================================================
// Deduplication Check
// ============================================================

/**
 * BUG-052 FIX: Ensure all synced transactions have fingerprints.
 * 
 * Problem: Transactions synced from other devices don't have fingerprints
 * (fingerprints are local-only, not synced to PostgreSQL). This causes
 * duplicate imports when the same file is imported on multiple devices.
 * 
 * Solution: Before checking for duplicates, generate fingerprints for any
 * transactions that don't have them. This handles cross-device deduplication
 * transparently without requiring schema or sync changes.
 */
async function ensureSyncedTransactionsHaveFingerprints(): Promise<number> {
  // Find transactions without fingerprints (likely synced from other devices)
  const txsWithoutFingerprint = await db.transactions
    .filter(tx => !tx.importFingerprint && tx.date && tx.amount !== undefined)
    .toArray();

  if (txsWithoutFingerprint.length === 0) {
    return 0;
  }

  // Generate fingerprints in a transaction for atomicity
  await db.transaction('rw', db.transactions, async () => {
    for (const tx of txsWithoutFingerprint) {
      const date = tx.date instanceof Date ? tx.date : new Date(tx.date);
      if (isNaN(date.getTime())) continue;
      
      const fingerprint = generateFingerprint(date, tx.amount, tx.description || '');
      await db.transactions.update(tx.id!, { importFingerprint: fingerprint });
    }
  });

  devlog.info('import', `[BUG-052] Generated fingerprints for ${txsWithoutFingerprint.length} synced transactions`);
  return txsWithoutFingerprint.length;
}

/** Batched importFingerprint anyOf query. */
async function queryStoredByFingerprint(values: string[]): Promise<Transaction[]> {
  const found: Transaction[] = [];
  for (let i = 0; i < values.length; i += 100) {
    const batch = values.slice(i, i + 100);
    found.push(
      ...(await db.transactions.where('importFingerprint').anyOf(batch).toArray())
    );
  }
  return found;
}

/**
 * Determine which candidate transactions are duplicates of stored ones.
 * Returns the set of candidate `fingerprint` values that are duplicates
 * (callers keep checking `set.has(tx.fingerprint)`).
 *
 * Two match paths (BUG-092 versioned fingerprints):
 * 1. Direct: stored fp2_ equals the candidate's fp2_ fingerprint.
 * 2. Legacy compat: stored fp_ (pre-2026-07-10 import) equals the
 *    candidate's LEGACY fingerprint. Legacy hashes of non-Latin
 *    descriptions collide (all empty), so a legacy match is only a
 *    CANDIDATE - it counts as a duplicate only after verifying the real
 *    tuple: same day, same amount, same v2-normalized description.
 */
export async function findDuplicateFingerprints(
  candidates: Array<Pick<ParsedTransaction, 'date' | 'amount' | 'description' | 'fingerprint'>>
): Promise<Set<string>> {
  const duplicates = new Set<string>();

  // Path 1: direct fp2 matches
  const storedV2 = await queryStoredByFingerprint(candidates.map((c) => c.fingerprint));
  const foundV2 = new Set(storedV2.map((t) => t.importFingerprint).filter(Boolean));
  for (const c of candidates) {
    if (foundV2.has(c.fingerprint)) duplicates.add(c.fingerprint);
  }

  // Path 2: legacy fp_ candidates + tuple verification
  const legacyByFp = new Map<string, typeof candidates>();
  for (const c of candidates) {
    if (duplicates.has(c.fingerprint)) continue;
    const legacy = generateLegacyFingerprint(c.date, c.amount, c.description);
    if (!legacyByFp.has(legacy)) legacyByFp.set(legacy, []);
    legacyByFp.get(legacy)!.push(c);
  }
  if (legacyByFp.size === 0) return duplicates;

  const storedLegacy = await queryStoredByFingerprint([...legacyByFp.keys()]);
  for (const stored of storedLegacy) {
    const matches = legacyByFp.get(stored.importFingerprint || '');
    if (!matches) continue;
    const storedDate = stored.date instanceof Date ? stored.date : new Date(stored.date);
    if (isNaN(storedDate.getTime())) continue;
    const storedDay = storedDate.toISOString().split('T')[0];
    const storedAmount = Math.abs(stored.amount).toFixed(2);
    const storedDesc = normalizeDescriptionV2(stored.description || '');
    for (const c of matches) {
      if (
        c.date.toISOString().split('T')[0] === storedDay &&
        Math.abs(c.amount).toFixed(2) === storedAmount &&
        normalizeDescriptionV2(c.description) === storedDesc
      ) {
        duplicates.add(c.fingerprint);
      }
    }
  }

  return duplicates;
}

/**
 * Check if a FITID already exists.
 */
async function findExistingFitIds(fitIds: string[]): Promise<Set<string>> {
  const existing = new Set<string>();

  for (let i = 0; i < fitIds.length; i += 100) {
    const batch = fitIds.slice(i, i + 100);
    const found = await db.transactions
      .where('importFitId')
      .anyOf(batch)
      .toArray();

    for (const tx of found) {
      if (tx.importFitId) {
        existing.add(tx.importFitId);
      }
    }
  }

  return existing;
}

// ============================================================
// Main Parse & Preview Function
// ============================================================

/**
 * Parse a file and return a preview without importing.
 */
export async function parseFileForPreview(file: File): Promise<ImportPreview> {
  const content = await file.text();
  const format = detectFormat(file, content);

  const preview: ImportPreview = {
    format,
    fileName: file.name,
    fileSize: file.size,
    totalTransactions: 0,
    transactions: [],
    duplicateCount: 0,
    newCount: 0,
    duplicateFingerprints: new Set(),
    errors: [],
    warnings: [],
  };

  if (format === 'unknown') {
    preview.errors.push('Unable to detect file format. Supported formats: OFX, QFX, QIF, CSV, JSON');
    return preview;
  }

  try {
    let transactions: ParsedTransaction[] = [];

    switch (format) {
      case 'ofx':
      case 'qfx':
        transactions = await parseOFX(content);
        break;

      case 'qif':
        transactions = await parseQIF(content);
        break;

      case 'csv': {
        // Parse headers first
        const lines = content.split('\n');
        const headerLine = lines[0] || '';
        const headers = headerLine.split(',').map(h => h.replace(/^\uFEFF/, '').replace(/"/g, '').trim());

        // Check if this is a 1Money CSV (has special multi-account structure)
        if (is1MoneyCSV(headers)) {
          preview.is1MoneyFormat = true;
          preview.rawFileContent = content;  // Store for specialized import
          
          // Count transactions for preview (rough estimate from non-empty rows)
          const dataRows = lines.slice(1).filter(line => {
            const trimmed = line.trim();
            return trimmed && trimmed.split(',')[0]?.trim(); // Has a date
          });
          
          // Parse a few for preview display
          const preset = detectBankPreset(headers);
          preview.bankPreset = preset;
          preview.columnMapping = preset?.columnMapping || autoDetectColumns(headers);
          
          // Get sample transactions for preview
          transactions = await parseCSV(content, preview.columnMapping, preset);
          
          // Add 1Money-specific info to warnings (displayed as info)
          preview.warnings.push(`1Money format detected: ${dataRows.length} transactions with multi-account support`);
          preview.warnings.push('Accounts will be auto-created from FROM ACCOUNT column');
          preview.warnings.push('Transfers between accounts will be preserved');
        } else {
          // Standard CSV - detect bank preset
          const preset = detectBankPreset(headers);
          if (preset) {
            preview.bankPreset = preset;
            preview.columnMapping = preset.columnMapping;
          } else {
            // Auto-detect columns
            preview.columnMapping = autoDetectColumns(headers);
          }

          transactions = await parseCSV(content, preview.columnMapping, preset);
        }
        break;
      }

      case 'json': {
        const data = JSON.parse(content);
        
        // Check if this is a full NinjaCash backup (has accounts AND transactions)
        const isFullBackup = data.accounts && Array.isArray(data.accounts) && 
                             data.transactions && Array.isArray(data.transactions);
        
        if (isFullBackup) {
          // Full NinjaCash backup - will restore accounts, categories, transactions
          preview.isNinjaCashBackup = true;
          preview.backupData = data as NinjaCashBackupData;
          
          // Count non-deleted transactions for preview
          const validTransactions = (data.transactions as Array<{ date: string | Date; deletedAt?: string | Date | null }>)
            .filter(tx => !tx.deletedAt);
          
          for (const tx of validTransactions) {
            const txData = tx as NinjaCashBackupData['transactions'][0];
            const date = new Date(txData.date);
            if (isNaN(date.getTime())) continue;
            
            transactions.push({
              date,
              amount: txData.amount,
              type: txData.type as 'income' | 'expense' | 'transfer',
              description: txData.description,
              fingerprint: txData.importFingerprint || generateFingerprint(date, txData.amount, txData.description),
              rawData: txData,
            });
          }
          
          // For full backups, we don't check duplicates since we're doing a full restore
          preview.warnings.push(`Full NinjaCash backup detected: ${data.accounts.length} accounts, ${data.categories?.length || 0} categories, ${validTransactions.length} transactions will be imported`);
        } else if (data.transactions && Array.isArray(data.transactions)) {
          // Transaction-only JSON (legacy or external format)
          for (const tx of data.transactions) {
            const date = new Date(tx.date);
            if (isNaN(date.getTime())) continue;

            transactions.push({
              date,
              amount: tx.amount,
              type: tx.type,
              description: tx.description,
              fingerprint: generateFingerprint(date, tx.amount, tx.description),
              rawData: tx,
            });
          }
        } else {
          preview.errors.push('JSON file does not contain a valid NinjaCash backup or transactions array');
        }
        break;
      }
    }

    preview.transactions = transactions;
    preview.totalTransactions = transactions.length;

    // BUG-052 FIX: Ensure synced transactions have fingerprints for cross-device deduplication
    await ensureSyncedTransactionsHaveFingerprints();

    // Check for duplicates (fp2 direct + legacy fp_ verified matches - BUG-092)
    const existingFingerprints = await findDuplicateFingerprints(transactions);

    // Also check FITIDs for OFX
    const fitIds = transactions.filter(t => t.fitId).map(t => t.fitId!);
    const existingFitIds = fitIds.length > 0 ? await findExistingFitIds(fitIds) : new Set<string>();

    for (const tx of transactions) {
      if (existingFingerprints.has(tx.fingerprint) || (tx.fitId && existingFitIds.has(tx.fitId))) {
        preview.duplicateCount++;
        preview.duplicateFingerprints.add(tx.fingerprint);
      } else {
        preview.newCount++;
      }
    }

    devlog.info('import', 'Parse preview complete', {
      format,
      total: preview.totalTransactions,
      duplicates: preview.duplicateCount,
      new: preview.newCount,
    });

  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    preview.errors.push(message);
    devlog.error('import', 'Parse preview failed', { error: message });
  }

  return preview;
}

// ============================================================
// Main Import Function
// ============================================================

/**
 * Import parsed transactions into the database.
 */
export async function importTransactions(
  preview: ImportPreview,
  options: UniversalImportOptions
): Promise<UniversalImportResult> {
  const result: UniversalImportResult = {
    success: false,
    imported: 0,
    skipped: 0,
    duplicatesSkipped: 0,
    errors: [],
    warnings: [],
    newAccounts: [],
    newCategories: [],
  };

  if (!options.accountId) {
    result.errors.push('No target account selected');
    return result;
  }

  const account = await db.accounts.get(options.accountId);
  if (!account) {
    result.errors.push('Target account not found');
    return result;
  }

  const skipDuplicates = options.skipDuplicates ?? true;
  const importSource = options.importSource || `${preview.format}-import`;

  devlog.info('import', 'Starting import', {
    accountId: options.accountId,
    total: preview.totalTransactions,
    skipDuplicates,
  });

  try {
    const transactionsToImport: TransactionInput[] = [];

    for (const tx of preview.transactions) {
      // Skip duplicates if enabled
      if (skipDuplicates && preview.duplicateFingerprints.has(tx.fingerprint)) {
        result.duplicatesSkipped++;
        continue;
      }

      // Auto-detect category from description if not already set
      // This handles grocery stores, convenience stores, etc.
      if (!tx.categoryName) {
        const detectedCategory = detectGroceryCategory(tx.description);
        if (detectedCategory) {
          tx.categoryName = detectedCategory;
        }
      }

      // Get or create category if specified (or auto-detected above)
      let categoryId: string | undefined;
      if (tx.categoryName) {
        let category = await db.categories
          .where('name')
          .equalsIgnoreCase(tx.categoryName)
          .first();

        if (!category) {
          const newCategoryId = generateId();
          await db.categories.add({
            id: newCategoryId,
            name: tx.categoryName,
            type: tx.type === 'income' ? 'income' : 'expense',
            isActive: true,
          });
          result.newCategories.push(tx.categoryName);
          categoryId = newCategoryId;
        } else {
          categoryId = category.id;
        }
      }

      transactionsToImport.push({
        accountId: options.accountId,
        type: tx.type,
        amount: tx.amount,
        description: tx.description,
        date: tx.date,
        categoryId,
        currency: account.currency,
        importFingerprint: tx.fingerprint,
        importFitId: tx.fitId,
        importSource,
      });
    }

    // Bulk create transactions
    if (transactionsToImport.length > 0) {
      await transactionService.bulkCreate(transactionsToImport);
      result.imported = transactionsToImport.length;
    }

    result.success = true;
    result.skipped = preview.totalTransactions - result.imported - result.duplicatesSkipped;

    // Emit events and trigger sync
    dataEvents.emit('transactions');
    dataEvents.emit('accounts');
    if (result.newCategories.length > 0) {
      dataEvents.emit('categories');
    }
    syncManager.triggerPush();

    devlog.info('import', 'Import complete', result);

  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    result.errors.push(message);
    devlog.error('import', 'Import failed', { error: message });
  }

  return result;
}

// ============================================================
// NinjaCash Full Backup Import
// ============================================================

/**
 * Import a full NinjaCash backup (accounts, categories, transactions).
 * This restores a complete backup exported from NinjaCash.
 */
export async function importNinjaCashBackup(
  backupData: NinjaCashBackupData
): Promise<UniversalImportResult> {
  const result: UniversalImportResult = {
    success: false,
    imported: 0,
    skipped: 0,
    duplicatesSkipped: 0,
    errors: [],
    warnings: [],
    newAccounts: [],
    newCategories: [],
  };

  devlog.info('import', 'Starting NinjaCash backup import', {
    accounts: backupData.accounts?.length || 0,
    categories: backupData.categories?.length || 0,
    transactions: backupData.transactions?.length || 0,
  });

  try {
    const now = new Date();

    // IMP-002 FIX: Wrap entire restore in a single transaction for atomicity
    // If any step fails, all changes are rolled back automatically
    await db.transaction('rw', [
      db.categories,
      db.accounts, 
      db.transactions,
      db.scheduledTransactions,
      db.skippedOccurrences,
    ], async () => {

    // Step 1: Import categories first (transactions reference them)
    // IMP-001 FIX: Batch operations - prefetch existing IDs, then bulkAdd new ones
    if (backupData.categories && backupData.categories.length > 0) {
      const categoryIds = backupData.categories.map(c => c.id);
      const existingCategoryIds = new Set(
        (await db.categories.where('id').anyOf(categoryIds).toArray()).map(c => c.id)
      );

      const categoriesToAdd = backupData.categories
        .filter(cat => !existingCategoryIds.has(cat.id))
        .map(cat => ({
          id: cat.id,
          name: cat.name,
          type: cat.type as 'income' | 'expense',
          icon: cat.icon,
          color: cat.color,
          parentId: cat.parentId,
          isActive: cat.isActive ?? true,
          createdAt: cat.createdAt ? new Date(cat.createdAt) : now,
          updatedAt: cat.updatedAt ? new Date(cat.updatedAt) : now,
        }));

      if (categoriesToAdd.length > 0) {
        await db.categories.bulkAdd(categoriesToAdd);
        result.newCategories = categoriesToAdd.map(c => c.name);
      }
      devlog.info('import', `Imported ${result.newCategories.length} categories`);
    }

    // Step 2: Import accounts
    // IMP-001 FIX: Batch operations - prefetch existing IDs, then bulkAdd new ones
    if (backupData.accounts && backupData.accounts.length > 0) {
      const accountIds = backupData.accounts.map(a => a.id);
      const existingAccountIds = new Set(
        (await db.accounts.where('id').anyOf(accountIds).toArray()).map(a => a.id)
      );

      const accountsToAdd = backupData.accounts
        .filter(acc => !existingAccountIds.has(acc.id))
        .map(acc => ({
          id: acc.id,
          name: acc.name,
          fullName: acc.fullName,
          type: acc.type as 'checking' | 'savings' | 'cash' | 'credit' | 'crypto' | 'investment',
          currency: acc.currency,
          balance: acc.balance,
          initialBalance: acc.initialBalance ?? 0, // BUG-058 FIX
          isActive: acc.isActive ?? true,
          sortOrder: acc.sortOrder,
          createdAt: acc.createdAt ? new Date(acc.createdAt) : now,
          updatedAt: acc.updatedAt ? new Date(acc.updatedAt) : now,
          syncStatus: 'pending' as const, // Mark as pending so it syncs to server
        }));

      if (accountsToAdd.length > 0) {
        await db.accounts.bulkAdd(accountsToAdd);
        result.newAccounts = accountsToAdd.map(a => a.name);
      }
      devlog.info('import', `Imported ${result.newAccounts.length} accounts`);
    }

    // Step 3: Import transactions
    // IMP-001 FIX: Batch operations - prefetch existing IDs, then bulkAdd new ones
    if (backupData.transactions && backupData.transactions.length > 0) {
      // Filter out soft-deleted and validate dates first
      const validTransactions: typeof backupData.transactions = [];
      let skippedCount = 0;

      for (const tx of backupData.transactions) {
        if (tx.deletedAt) {
          skippedCount++;
          continue;
        }
        const txDate = new Date(tx.date);
        if (isNaN(txDate.getTime())) {
          result.warnings.push(`Skipped transaction with invalid date: ${tx.description}`);
          skippedCount++;
          continue;
        }
        validTransactions.push(tx);
      }

      // Prefetch existing transaction IDs
      const txIds = validTransactions.map(t => t.id);
      const existingTxIds = new Set(
        (await db.transactions.where('id').anyOf(txIds).toArray()).map(t => t.id)
      );

      // Filter to new transactions and map to DB format
      const transactionsToAdd = validTransactions
        .filter(tx => !existingTxIds.has(tx.id))
        .map(tx => ({
          id: tx.id,
          accountId: tx.accountId,
          type: tx.type as 'income' | 'expense' | 'transfer',
          amount: tx.amount,
          categoryId: tx.categoryId,
          toAccountId: tx.toAccountId,
          description: tx.description,
          date: new Date(tx.date),
          currency: tx.currency,
          createdAt: tx.createdAt ? new Date(tx.createdAt) : now,
          updatedAt: tx.updatedAt ? new Date(tx.updatedAt) : now,
          syncStatus: 'pending' as const,
          scheduledTransactionId: tx.scheduledTransactionId,
          wasSkipped: tx.wasSkipped,
          importFingerprint: tx.importFingerprint,
          importFitId: tx.importFitId,
          importSource: tx.importSource || 'ninjacash-backup',
        }));

      // Count skipped due to existing
      skippedCount += validTransactions.length - transactionsToAdd.length;

      if (transactionsToAdd.length > 0) {
        await db.transactions.bulkAdd(transactionsToAdd);
      }

      result.imported = transactionsToAdd.length;
      result.skipped = skippedCount;
      devlog.info('import', `Imported ${transactionsToAdd.length} transactions, skipped ${skippedCount}`);
    }

    // Step 4: Import scheduledTransactions (BUG-054 fix)
    // Deduplicate by detectionKey to handle historical duplicates from BUG-053
    // IMP-001 FIX: Batch operations for performance
    if (backupData.scheduledTransactions && backupData.scheduledTransactions.length > 0) {
      let scheduledSkipped = 0;
      let duplicatesRemoved = 0;

      // Group by detectionKey to deduplicate (in-memory, fast)
      const byDetectionKey = new Map<string, typeof backupData.scheduledTransactions>();
      const noKey: typeof backupData.scheduledTransactions = [];

      for (const sched of backupData.scheduledTransactions) {
        const key = (sched.detectionKey || '').toLowerCase().trim();
        if (key) {
          if (!byDetectionKey.has(key)) {
            byDetectionKey.set(key, []);
          }
          byDetectionKey.get(key)!.push(sched);
        } else {
          noKey.push(sched);
        }
      }

      // Pick best from each group (prefer isIgnoredPermanently, then non-deleted, then most recent)
      const deduped: typeof backupData.scheduledTransactions = [];
      
      for (const [, records] of byDetectionKey) {
        if (records.length === 1) {
          deduped.push(records[0]);
        } else {
          duplicatesRemoved += records.length - 1;
          records.sort((a, b) => {
            if (a.isIgnoredPermanently && !b.isIgnoredPermanently) return -1;
            if (!a.isIgnoredPermanently && b.isIgnoredPermanently) return 1;
            if (!a.deletedAt && b.deletedAt) return -1;
            if (a.deletedAt && !b.deletedAt) return 1;
            const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
            const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
            return bTime - aTime;
          });
          deduped.push(records[0]);
        }
      }
      deduped.push(...noKey);

      // Filter out soft-deleted (unless isIgnoredPermanently) and validate dates
      const validScheduled: Array<typeof deduped[0] & { parsedNextDueDate: Date }> = [];
      for (const sched of deduped) {
        if (sched.deletedAt && !sched.isIgnoredPermanently) {
          scheduledSkipped++;
          continue;
        }
        const nextDueDate = new Date(sched.nextDueDate);
        if (isNaN(nextDueDate.getTime())) {
          result.warnings.push(`Skipped scheduled transaction with invalid nextDueDate: ${sched.description || sched.id}`);
          scheduledSkipped++;
          continue;
        }
        validScheduled.push({ ...sched, parsedNextDueDate: nextDueDate });
      }

      // IMP-001 FIX: Batch prefetch existing IDs and detectionKeys
      // Only fetch what we need - avoid loading entire table
      const schedIds = validScheduled.map(s => s.id);
      const existingSchedIds = new Set<string>();
      if (schedIds.length > 0) {
        const found = await db.scheduledTransactions.where('id').anyOf(schedIds).toArray();
        found.forEach(s => existingSchedIds.add(s.id!));
      }
      
      // Only fetch detectionKeys we're trying to import (not entire table)
      const inputDetectionKeys = validScheduled
        .filter(s => s.detectionKey)
        .map(s => s.detectionKey!.toLowerCase().trim());
      const existingDetectionKeys = new Set<string>();
      if (inputDetectionKeys.length > 0) {
        // Query only records with matching detectionKeys
        const allExisting = await db.scheduledTransactions
          .filter(s => s.detectionKey != null)
          .toArray();
        allExisting.forEach(s => {
          if (s.detectionKey) {
            existingDetectionKeys.add(s.detectionKey.toLowerCase().trim());
          }
        });
      }

      // Track seen detectionKeys within this batch to prevent intra-batch duplicates
      const seenDetectionKeys = new Set<string>();

      // Filter to records that don't exist by ID or detectionKey
      const scheduledToAdd = validScheduled
        .filter(sched => {
          if (existingSchedIds.has(sched.id)) {
            scheduledSkipped++;
            return false;
          }
          if (sched.detectionKey) {
            const normalizedKey = sched.detectionKey.toLowerCase().trim();
            // Check existing DB keys
            if (existingDetectionKeys.has(normalizedKey)) {
              scheduledSkipped++;
              return false;
            }
            // Check intra-batch duplicates
            if (seenDetectionKeys.has(normalizedKey)) {
              scheduledSkipped++;
              return false;
            }
            seenDetectionKeys.add(normalizedKey);
          }
          return true;
        })
        .map(sched => {
          const lastGeneratedDate = sched.lastGeneratedDate ? new Date(sched.lastGeneratedDate) : undefined;
          const pausedUntil = sched.pausedUntil ? new Date(sched.pausedUntil) : undefined;
          const endDate = sched.endDate ? new Date(sched.endDate) : undefined;
          const deletedAt = sched.deletedAt ? new Date(sched.deletedAt) : undefined;

          return {
            id: sched.id,
            accountId: sched.accountId,
            categoryId: sched.categoryId,
            toAccountId: sched.toAccountId,
            type: sched.type as 'income' | 'expense' | 'transfer',
            amount: sched.amount,
            description: sched.description || '',
            frequency: sched.frequency as 'weekly' | 'biweekly' | 'monthly' | 'yearly',
            dayOfMonth: sched.dayOfMonth,
            dayOfWeek: sched.dayOfWeek,
            monthOfYear: sched.monthOfYear,
            nextDueDate: sched.parsedNextDueDate,
            lastGeneratedDate: lastGeneratedDate && !isNaN(lastGeneratedDate.getTime()) ? lastGeneratedDate : undefined,
            isPaused: sched.isPaused ?? false,
            pausedUntil: pausedUntil && !isNaN(pausedUntil.getTime()) ? pausedUntil : undefined,
            isIgnoredPermanently: sched.isIgnoredPermanently ?? false,
            endDate: endDate && !isNaN(endDate.getTime()) ? endDate : undefined,
            autoDetected: sched.autoDetected ?? true,
            detectionKey: sched.detectionKey,
            dismissCount: sched.dismissCount ?? 0,
            createdAt: sched.createdAt ? new Date(sched.createdAt) : now,
            updatedAt: sched.updatedAt ? new Date(sched.updatedAt) : now,
            syncStatus: 'pending' as const,
            deletedAt: deletedAt && !isNaN(deletedAt.getTime()) ? deletedAt : undefined,
          };
        });

      if (scheduledToAdd.length > 0) {
        await db.scheduledTransactions.bulkAdd(scheduledToAdd);
      }

      if (duplicatesRemoved > 0) {
        result.warnings.push(`Deduplicated ${duplicatesRemoved} duplicate scheduled transactions`);
      }
      devlog.info('import', `Imported ${scheduledToAdd.length} scheduled transactions, skipped ${scheduledSkipped}, deduped ${duplicatesRemoved}`);
    }

    // Step 5: Import skippedOccurrences
    // IMP-001 FIX: Batch operations for performance
    if (backupData.skippedOccurrences && backupData.skippedOccurrences.length > 0) {
      let skippedOccSkipped = 0;

      // Step 5a: Validate dates and prepare records
      const validOccurrences = backupData.skippedOccurrences
        .map(skip => {
          const skippedDate = new Date(skip.skippedDate);
          if (isNaN(skippedDate.getTime())) {
            result.warnings.push(`Skipped occurrence with invalid date: ${skip.id}`);
            skippedOccSkipped++;
            return null;
          }
          return { ...skip, parsedSkippedDate: skippedDate };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);

      // Step 5b: Batch fetch existing IDs
      const occIds = validOccurrences.map(s => s.id);
      const existingOccIds = new Set<string>();
      if (occIds.length > 0) {
        const found = await db.skippedOccurrences.where('id').anyOf(occIds).toArray();
        found.forEach(s => existingOccIds.add(s.id!));
      }

      // Step 5c: Batch fetch candidates for same-day duplicate check
      // Get all existing occurrences for the scheduledTransactionIds we're importing
      const parentIds = [...new Set(validOccurrences.map(s => s.scheduledTransactionId))];
      const existingCandidates: Array<{ scheduledTransactionId: string; skippedDate: Date }> = [];
      if (parentIds.length > 0) {
        const candidates = await db.skippedOccurrences
          .filter(s => parentIds.includes(s.scheduledTransactionId))
          .toArray();
        candidates.forEach(c => {
          existingCandidates.push({
            scheduledTransactionId: c.scheduledTransactionId,
            skippedDate: new Date(c.skippedDate),
          });
        });
      }

      // Step 5d: Filter in-memory and track intra-batch duplicates
      const seenInBatch = new Set<string>(); // "schedId|day" format
      const getDayKey = (schedId: string, date: Date) => 
        `${schedId}|${date.toISOString().slice(0, 10)}`;

      const occurrencesToAdd = validOccurrences
        .filter(skip => {
          // Check existing by ID
          if (existingOccIds.has(skip.id)) {
            skippedOccSkipped++;
            return false;
          }

          const skipTime = skip.parsedSkippedDate.getTime();
          const dayKey = getDayKey(skip.scheduledTransactionId, skip.parsedSkippedDate);

          // Check same-day duplicate in existing DB records
          const isDupeInDb = existingCandidates.some(c =>
            c.scheduledTransactionId === skip.scheduledTransactionId &&
            Math.abs(c.skippedDate.getTime() - skipTime) < 86400000
          );
          if (isDupeInDb) {
            skippedOccSkipped++;
            return false;
          }

          // Check intra-batch duplicate
          if (seenInBatch.has(dayKey)) {
            skippedOccSkipped++;
            return false;
          }
          seenInBatch.add(dayKey);

          return true;
        })
        .map(skip => ({
          id: skip.id,
          scheduledTransactionId: skip.scheduledTransactionId,
          skippedDate: skip.parsedSkippedDate,
          reason: skip.reason,
          createdAt: skip.createdAt ? new Date(skip.createdAt) : now,
        }));

      // Step 5e: Bulk add
      if (occurrencesToAdd.length > 0) {
        await db.skippedOccurrences.bulkAdd(occurrencesToAdd);
      }

      devlog.info('import', `Imported ${occurrencesToAdd.length} skipped occurrences, skipped ${skippedOccSkipped}`);
    }

    }); // End of IMP-002 atomic transaction

    result.success = true;

    // Emit events and trigger sync
    dataEvents.emit('all');
    syncManager.triggerPush();

    devlog.info('import', 'NinjaCash backup import complete', result);

  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    result.errors.push(message);
    devlog.error('import', 'NinjaCash backup import failed', { error: message });
  }

  return result;
}

// ============================================================
// Get Bank Presets by Country
// ============================================================

export function getBankPresetsByCountry(): Map<string, BankPreset[]> {
  const byCountry = new Map<string, BankPreset[]>();

  for (const preset of BANK_PRESETS) {
    const existing = byCountry.get(preset.country) || [];
    existing.push(preset);
    byCountry.set(preset.country, existing);
  }

  return byCountry;
}

export function getBankPresetById(id: string): BankPreset | undefined {
  return BANK_PRESETS.find(p => p.id === id);
}
